import { useCallback, useEffect, useState } from 'react';
import { isUserHasKey } from '../../GlobalFunctions';
import DistrictSelector from '../AdminReportComponents/DistrictSelector';
import SchoolSelector from '../AdminReportComponents/SchoolSelector';
import { Icon } from '@iconify/react';
import { flushLocalForage, getLocalForage, setLocalForage } from '../../aaa';
import { useSelector } from 'react-redux';
import { RootState } from '../../ApplicationState';
import { SchoolModel, UserProfileType } from '../../../peekapak-types/DataProtocolTypes';
import {
  getDistricts,
  getJob,
  getJournals as getJournalsFromDb,
  getSchools,
  getUsers,
  queryMixpanelByGroup,
  queryMixpanelByGroupV2,
} from '../../BackendInterface';
import { logger } from '../../error-tracker';
import Spinner from 'react-spinkit';
import SpinnerOverlay from '../../SpinnerOverlay';
import {
  getLegacySchoolPeekavilleScript,
  getLegacySchoolPlatformScript,
  getMixpanelDatabaseByDate,
  getStudentsToLoginsMap,
} from '../reportUtilities';
import { District, ReportingMixpanelEvent, SchoolsList } from '../../../peekapak-types/ReportDataExchangeTypes';
import { PeekavilleJournalEntry } from '../../../peekapak-types/PeekavilleGameDataTypes';
import PeriodSelector from './PeriodSelector';
import ChartSegmentElementary from './ChartSegmentElementary';
import ChartSegmentSecondary from './ChartSegmentSecondary';
import styles from './MoodboardAdmin.module.scss';
import {
  getBeginningAndEndingOfFourWeek,
  getBeginningAndEndingOfQuarter,
  getBeginningAndEndingOfSemiYear,
  getBeginningAndEndingOfWeek,
  getBeginningAndEndingOfYear,
  getBeginningAndEndingOfSchoolYear,
  getBeginningAndEndingOfSchoolSemiYear,
  getBeginningAndEndingOfSchoolQuarter,
  getUpdatedSchoolUnifiedMoodScript,
  parseSchoolJournalChartSchoolYearly,
} from './moodboardUtilities';
import { getDefaultSchoolYear } from '../reportUtilities';
import SchoolYearSelector from './SchoolYearSelector';
import LoadingBar from '../../components/LoadingBar';

interface MoodboardAdminState {
  legacyPlatformEvents: ReportingMixpanelEvent[];
  legacyPlatformEventsReady: boolean;
  legacyPeekavilleEvents: ReportingMixpanelEvent[];
  legacyPeekavilleEventsReady: boolean;
  newMixpanelEvents: ReportingMixpanelEvent[];
  newMixpanelEventsReady: boolean;
  disableChangeLocation?: boolean;
  districtsList: District[] | Record<string, unknown>[];
  districtsListReady: boolean;
  errorMessage: string;
  journalsMap: string[];
  journalsMapReady: boolean;
  journalsMapLoadProgress?: string;
  numOfJournalDataRetrived?: number;
  numOfStudentJournalData?: number;
  schoolsList: SchoolsList;
  selectedDistrict: string;
  schoolsListReady: boolean;
  selectedSchool: string;
  selectedSchoolYear: string;
  selectedStartingDate: string;
  selectedEndingDate: string;
  userList: {
    schoolId: string;
    list: UserProfileType[];
  };
  userListProgress: string;
  userListReady: boolean;
}

const tableOverrideSuffix = '';

const MoodboardSchoolAdmin = (): JSX.Element => {
  const defaultSchoolYear = getDefaultSchoolYear();

  const getCommonInitialState = () => {
    return {
      disableChangeLocation: true,
      errorMessage: '',
      journalsMap: [],
      journalsMapLoadProgress: '',
      journalsMapReady: false,
      legacyPeekavilleEvents: [],
      legacyPeekavilleEventsReady: false,
      legacyPlatformEvents: [],
      legacyPlatformEventsReady: false,
      newMixpanelEvents: [],
      newMixpanelEventsReady: false,
      numOfJournalDataRetrived: 0,
      numOfStudentJournalData: 0,
      userList: {
        list: [],
        schoolId: '',
      },
      userListProgress: '',
      userListReady: false,
    };
  };

  const getDistrictInitialState = () => {
    return {
      districtsList: [],
      districtsListReady: false,
      selectedDistrict: '',
    };
  };

  const getSchoolInitialState = () => {
    return {
      schoolsList: {
        district: '',
        list: [],
      },
      schoolsListReady: false,
      selectedSchool: '',
    };
  };

  const getTimePeriodInitialState = () => {
    const { beginningTimeOfSchoolYear, endingTimeOfSchoolYear } = getBeginningAndEndingOfSchoolYear(defaultSchoolYear);
    return {
      selectedSchoolYear: defaultSchoolYear,
      selectedStartingDate: beginningTimeOfSchoolYear.toISOString().split('T')[0],
      selectedEndingDate: endingTimeOfSchoolYear.toISOString().split('T')[0],
    };
  };

  const [state, setState] = useState<MoodboardAdminState>({
    ...getCommonInitialState(),
    ...getDistrictInitialState(),
    ...getSchoolInitialState(),
    ...getTimePeriodInitialState(),
  });

  //get beginning and ending time of each period
  const { beginningTimeOfSchoolYear, endingTimeOfSchoolYear } = getBeginningAndEndingOfSchoolYear(
    state.selectedSchoolYear,
  );
  const { beginingTimeOfSemiYear, endingTimeOfSemiYear } = getBeginningAndEndingOfSchoolSemiYear(
    state.selectedSchoolYear,
  );
  const { beginingTimeOfQuarter, endingTimeOfQuarter } = getBeginningAndEndingOfSchoolQuarter(state.selectedSchoolYear);
  const [startYear] = state.selectedSchoolYear.split('-').map(Number);
  const { beginningTimeOfFourWeek, endingTimeOfFourWeek } = getBeginningAndEndingOfFourWeek(startYear);
  const { beginningTimeOfWeek, endingTimeOfWeek } = getBeginningAndEndingOfWeek(startYear);

  // initial date range of each period
  const initialYearlyRange = {
    from: beginningTimeOfSchoolYear,
    to: endingTimeOfSchoolYear,
  };
  const initialSemiYearlyRange = {
    from: beginingTimeOfSemiYear,
    to: endingTimeOfSemiYear,
  };
  const initiaQuarterlyRange = {
    from: beginingTimeOfQuarter,
    to: endingTimeOfQuarter,
  };
  const initialFourWeekRange = {
    from: beginningTimeOfFourWeek,
    to: endingTimeOfFourWeek,
  };
  const initialWeeklyRange = {
    from: beginningTimeOfWeek,
    to: endingTimeOfWeek,
  };

  const [dateRange, setDateRange] = useState(initialYearlyRange);
  const [selectedPeriodType, setSelectedPeriodType] = useState('yearly');
  const [dateFilteredJournals, setDateFilteredJournals] = useState<string[]>([]);
  const [dateFilteredJournalsPrevious, setDateFilteredJournalsPrevious] = useState<string[]>([]);

  const userProfile = useSelector((state: RootState) => state.user.userProfile);

  const handleSetInitialDateRange = (period: string) => {
    switch (period) {
      case 'yearly':
        setDateRange(initialYearlyRange);
        break;
      case 'semi-yearly':
        setDateRange(initialSemiYearlyRange);
        break;
      case 'quarterly':
        setDateRange(initiaQuarterlyRange);
        break;
      case 'four-weekly':
        setDateRange(initialFourWeekRange);
        break;
      case 'weekly':
        setDateRange(initialWeeklyRange);
        break;
      default:
        break;
    }
  };

  const handleChangeTypeOfPeriod = (period: string) => {
    setSelectedPeriodType(period);
    handleSetInitialDateRange(period);
  };

  const getShiftedDateRange = (direction: string) => {
    const yearChange = direction === 'previous' ? -1 : 1;
    const semiYearChangeFrom = direction === 'previous' ? -6 : 6;
    const semiYearChangeTo = direction === 'previous' ? -5 : 7;
    const quarterChangeFrom = direction === 'previous' ? -3 : 3;
    const quarterChangeTo = direction === 'previous' ? -2 : 4;
    const fourWeekChange = direction === 'previous' ? -28 : 28;
    const weekChange = direction === 'previous' ? -7 : 7;

    switch (selectedPeriodType) {
      case 'yearly':
        const [currentStartYear, currentEndYear] = state.selectedSchoolYear.split('-').map(Number);
        const newSchoolYear =
          direction === 'previous'
            ? `${currentStartYear - 1}-${currentEndYear - 1}`
            : `${currentStartYear + 1}-${currentEndYear + 1}`;
        const { beginningTimeOfSchoolYear: newBeginning, endingTimeOfSchoolYear: newEnding } =
          getBeginningAndEndingOfSchoolYear(newSchoolYear);

        setDateRange({
          from: newBeginning,
          to: newEnding,
        });
        setState((prevState) => ({
          ...prevState,
          selectedSchoolYear: newSchoolYear,
          selectedStartingDate: newBeginning.toISOString().split('T')[0],
          selectedEndingDate: newEnding.toISOString().split('T')[0],
        }));
        break;
      case 'semi-yearly':
        setDateRange((prevState) => ({
          from: new Date(new Date(prevState.from).setMonth(new Date(prevState.from).getMonth() + semiYearChangeFrom)),
          to: new Date(new Date(prevState.to).setMonth(new Date(prevState.to).getMonth() + semiYearChangeTo, 0)),
        }));
        break;
      case 'quarterly':
        setDateRange((prevState) => ({
          from: new Date(new Date(prevState.from).setMonth(new Date(prevState.from).getMonth() + quarterChangeFrom)),
          to: new Date(new Date(prevState.to).setMonth(new Date(prevState.to).getMonth() + quarterChangeTo, 0)),
        }));
        break;
      case 'four-weekly':
        setDateRange((prevState) => {
          return {
            from: new Date(new Date(prevState.from).setDate(new Date(prevState.from).getDate() + fourWeekChange)),
            to: new Date(new Date(prevState.to).setDate(new Date(prevState.to).getDate() + fourWeekChange)),
          };
        });
        break;
      case 'weekly':
        setDateRange((prevState) => {
          return {
            from: new Date(new Date(prevState.from).setDate(new Date(prevState.from).getDate() + weekChange)),
            to: new Date(new Date(prevState.to).setDate(new Date(prevState.to).getDate() + weekChange)),
          };
        });
        break;
    }
  };

  const getPreviousDateRange = (currentDateRange: { from: Date; to: Date }) => {
    const semiYearChangeFrom = -6;
    const semiYearChangeTo = -5;
    const quarterChangeFrom = -3;
    const quarterChangeTo = -2;
    const fourWeekChange = -28;
    const weekChange = -7;

    let dateRange = {};

    switch (selectedPeriodType) {
      case 'semi-yearly':
        dateRange = {
          from: new Date(
            new Date(currentDateRange.from).setMonth(new Date(currentDateRange.from).getMonth() + semiYearChangeFrom),
          ),
          to: new Date(
            new Date(currentDateRange.to).setMonth(new Date(currentDateRange.to).getMonth() + semiYearChangeTo, 0),
          ),
        };
        break;
      case 'quarterly':
        dateRange = {
          from: new Date(
            new Date(currentDateRange.from).setMonth(new Date(currentDateRange.from).getMonth() + quarterChangeFrom),
          ),
          to: new Date(
            new Date(currentDateRange.to).setMonth(new Date(currentDateRange.to).getMonth() + quarterChangeTo, 0),
          ),
        };
        break;
      case 'four-weekly':
        dateRange = {
          from: new Date(
            new Date(currentDateRange.from).setDate(new Date(currentDateRange.from).getDate() + fourWeekChange),
          ),
          to: new Date(new Date(currentDateRange.to).setDate(new Date(currentDateRange.to).getDate() + fourWeekChange)),
        };
        break;
      case 'weekly':
        dateRange = {
          from: new Date(
            new Date(currentDateRange.from).setDate(new Date(currentDateRange.from).getDate() + weekChange),
          ),
          to: new Date(new Date(currentDateRange.to).setDate(new Date(currentDateRange.to).getDate() + weekChange)),
        };
        break;
    }
    return dateRange;
  };

  const shouldShowNavArrowRight = () => {
    const currentDate = new Date();
    const currentSchoolYear = getDefaultSchoolYear();

    if (selectedPeriodType === 'yearly') {
      return state.selectedSchoolYear < currentSchoolYear;
    }
    if (selectedPeriodType === 'semi-yearly' || selectedPeriodType === 'quarterly') {
      const [, endYear] = state.selectedSchoolYear.split('-').map(Number);
      if (dateRange.to.getFullYear() === currentDate.getFullYear()) {
        return dateRange.to.getMonth() < currentDate.getMonth();
      } else {
        return dateRange.to < currentDate;
      }
    }
    if (selectedPeriodType === 'four-weekly' || selectedPeriodType === 'weekly') {
      const { endingTimeOfSchoolYear } = getBeginningAndEndingOfSchoolYear(state.selectedSchoolYear);
      return (endingTimeOfSchoolYear.getTime() - dateRange.to.getTime()) / 86400000 > 7 ? true : false;
    }
    return false;
  };

  const shouldShowNavArrowLeft = () => {
    if (selectedPeriodType === 'yearly') {
      return true;
    }
    if (selectedPeriodType !== 'yearly') {
      const { beginningTimeOfSchoolYear } = getBeginningAndEndingOfSchoolYear(state.selectedSchoolYear);
      return (dateRange.from.getTime() - beginningTimeOfSchoolYear.getTime()) / 86400000 > 7 ? true : false;
    }
    return false;
  };

  //
  // get list of districts
  //
  useEffect(() => {
    let mounted = true;
    run();

    return function cleanup() {
      mounted = false;
    };

    async function run() {
      if (!state.districtsListReady) {
        getListOfDistricts();
      } else {
        await restoreSavedData();
      }
    }
    async function getListOfDistricts() {
      const storageKey = 'districtsList';
      const map = await getLocalForage(storageKey);

      if (map && mounted) {
        return setState((prevState) => ({
          ...prevState,
          districtsList: map,
          districtsListReady: true,
        }));
      }

      const mapFromDb = await getDistricts();
      await setLocalForage(storageKey, mapFromDb, 1440);

      if (mounted) {
        setState((prevState) => ({
          ...prevState,
          districtsList: mapFromDb,
          districtsListReady: true,
        }));
      }
    }

    async function restoreSavedData() {
      const storageKey = 'selectedDistrict';
      const storageKeySchool = 'selectedSchool';
      const selectedDistrict = await getLocalForage(storageKey);
      const selectedSchool = await getLocalForage(storageKeySchool);

      if (selectedDistrict) {
        if (mounted) {
          if (selectedSchool) {
            setState((prevState) => ({
              ...prevState,
              selectedDistrict,
              selectedSchool,
            }));
          } else {
            setState((prevState) => ({ ...prevState, selectedDistrict }));
          }
        }
      }
    }
  }, [
    state.districtsList,
    state.districtsListReady,
    userProfile?.district,
    userProfile?.schoolId,
    state.selectedDistrict,
  ]);
  //
  // schools in district
  //
  useEffect(() => {
    let mounted = true;
    if (!state.schoolsListReady) getListOfSchoolsInDistrict();
    return function cleanup() {
      mounted = false;
    };

    // load list of schools
    async function getListOfSchoolsInDistrict() {
      const districtStorageKey = 'selectedDistrict';
      const district =
        state.selectedDistrict || (await getLocalForage(districtStorageKey)) || userProfile?.district || '';
      if (!district) return;
      const storageKey = `${district}-schoolsList`;
      const list = await getLocalForage(storageKey)?.filter((s: SchoolModel) => !s.isArchived);

      if (list && mounted) {
        return setState((prevState) => ({
          ...prevState,
          schoolsList: {
            district,
            list,
          },
          schoolsListReady: true,
        }));
      }

      const listFromDb = (await getSchools(district, tableOverrideSuffix))?.filter((s) => !s.isArchived);
      await setLocalForage(storageKey, listFromDb, 43200);

      if (mounted) {
        setState((prevState) => ({
          ...prevState,
          schoolsList: {
            district,
            list: listFromDb,
          },
          schoolsListReady: true,
        }));
      }
    }
  }, [userProfile, state.schoolsListReady, state.selectedDistrict, state.districtsListReady]);
  //
  // get list of users in selected school
  //
  useEffect(() => {
    let mounted = true;
    run();

    return function cleanup() {
      mounted = false;
    };

    async function run() {
      if (state.selectedSchool && state.selectedSchoolYear) {
        const fromStorageForAll = await getLocalForage(`${state.selectedSchool}-users`);

        if (fromStorageForAll && mounted) {
          setState((prevState) => ({
            ...prevState,
            userList: {
              list: fromStorageForAll,
              schoolId: state.selectedSchool,
            },
            userListReady: true,
          }));
        } else {
          getUsersInSchool();
        }
      }
    }
    async function getUsersInSchool() {
      const jobId = await getUsers(state.selectedSchool, tableOverrideSuffix);
      let job: {
        status: string;
        data: string;
      } = await getJob(jobId);

      try {
        while (isJobPending(job)) {
          processPendingJob(job);
          await yieldAndWait();
          job = await getJob(jobId);
        }
      } catch (e) {
        const err = e as Error;
        setState((prevState) => ({
          ...prevState,
          errorMessage: err.message,
        }));
        logger.logException(err);

        throw err;
      }

      const data = JSON.parse(job.data);

      if (job.status === 'Done') {
        await setLocalForage(`${state.selectedSchool}-users`, data, 10080);

        if (mounted) {
          setState((prevState) => ({
            ...prevState,
            userList: {
              list: data,
              schoolId: state.selectedSchool,
            },
            userListReady: true,
          }));
        }
      } else if (job.status === 'Error') {
        if (mounted) {
          if (job.data) {
            const data = JSON.parse(job.data);
            setState((prevState) => ({
              ...prevState,
              errorMessage: data.message,
            }));
            logger.logException(new Error(data.message));
          } else {
            const e = new Error('Unknown error occurred while trying to retrieve list of users');
            setState((prevState) => ({
              ...prevState,
              errorMessage: e.message,
            }));
            logger.logException(e);
          }
        }
      } else {
        throw new Error(`Unexpected job status ${job.status}`);
      }
    }
    function isJobPending(job: { status: string }) {
      return job.status !== 'Done' && job.status !== 'Error';
    }

    function processPendingJob(job: { data: string }) {
      if (job.data) {
        const data = JSON.parse(job.data);

        if (mounted) {
          setState((prevState) => ({
            ...prevState,
            userListProgress: `Found ${data.numberOfResults} users`,
          }));
        }
      }
    }

    function yieldAndWait() {
      return new Promise<void>((resolve) => {
        setTimeout(() => resolve(), 1000);
      });
    }
  }, [state.selectedSchool, state.selectedSchoolYear, state.selectedStartingDate, state.selectedEndingDate]);

  //
  // set selected district
  //
  useEffect(() => {
    run();
    async function run() {
      if (state.districtsListReady && state.districtsList.length > 0) {
        const storageKey = 'selectedDistrict';
        const selectedDistrict = await getLocalForage(storageKey);

        if (selectedDistrict) {
          setState((prevState) => ({ ...prevState, selectedDistrict }));
        } else {
          if (userProfile?.district) {
            if (state.districtsList.map((d) => d.id).includes(userProfile.district)) {
              return setState((prevState) => ({
                ...prevState,
                selectedDistrict: userProfile.district!,
              }));
            }
          }
        }
        return;
      }
      setState((prevState) => ({ ...prevState, selectedDistrict: '' }));
    }
  }, [userProfile?.district, state.districtsListReady, state.districtsList]);

  //
  // set selected school
  //
  useEffect(() => {
    run();
    async function run() {
      if (state.selectedDistrict && state.schoolsListReady && state.schoolsList.list.length > 0) {
        const district = state.selectedDistrict || userProfile?.district;
        const storageKey = `${district}-selectedSchool`;
        const selectedSchool = (async () => {
          // if user is not part of a district return nothing
          if (!userProfile?.district) {
            return '';
          }

          // if user is part of district and can change schools then return cache
          // or userProfile school Id
          if (isUserHasKey('KEY_ADMINISTRATOR_REPORTS_SELECT_SCHOOL')) {
            return (await getLocalForage(storageKey)) || state.schoolsList.list[0].id;
          }

          // if user is part of district but cannot change schools, then return
          // userProfile's schoolId or blank
          return (await getLocalForage(storageKey)) || userProfile.schoolId || '';
        })();

        setState((prevState) => ({ ...prevState, selectedSchool }));

        return;
      }

      setState((prevState) => ({ ...prevState, selectedSchool: '' }));
    }
  }, [
    state.schoolsListReady,
    state.schoolsList.list,
    state.selectedDistrict,
    userProfile?.district,
    userProfile?.schoolId,
  ]);

  //
  // get legacy platform events for this school
  //
  useEffect(() => {
    let mounted = true;
    run();
    return function cleanup() {
      mounted = false;
    };

    async function run() {
      const selectedStartingDate = state.selectedStartingDate;
      const selectedEndingDate = state.selectedEndingDate;
      const storageKey = `${state.selectedSchool}-legacyPlatformEvents-from-${selectedStartingDate}-to-${selectedEndingDate}`;

      if (state.selectedSchool && state.schoolsListReady) {
        const data = await getLocalForage(storageKey);
        if (!data) {
          doQuery();
        } else {
          if (mounted) {
            setState((prevState) => ({
              ...prevState,
              legacyPlatformEvents: data,
              legacyPlatformEventsReady: true,
            }));
          }
        }
      }

      async function doQuery() {
        const selectedSchoolObject = state.schoolsList.list.find((s) => s.id === state.selectedSchool);

        if (!selectedSchoolObject)
          throw new Error(
            `Unexpected condition: could not find a matching school object for selected school in report`,
          );

        try {
          const result = await queryMixpanelByGroup(
            getLegacySchoolPlatformScript(
              selectedStartingDate,
              selectedEndingDate,
              selectedSchoolObject.combineIds || selectedSchoolObject.id,
            ),
            'peekapak',
            tableOverrideSuffix,
          );

          await setLocalForage(storageKey, result, 60);

          if (mounted) {
            setState((prevState) => ({
              ...prevState,
              legacyPlatformEvents: result,
              legacyPlatformEventsReady: true,
            }));
          }
        } catch (error) {
          if (mounted) {
            setState((prevState) => ({
              ...prevState,
              errorMessage: (error as Error).message,
            }));
          }
        }
      }
    }
  }, [state.selectedSchool, state.selectedSchoolYear, state.selectedStartingDate, state.selectedEndingDate]);

  //
  // get legacy Peekaville events for this school
  //
  useEffect(() => {
    let mounted = true;

    if (state.schoolsListReady && state.selectedSchool && !state.legacyPeekavilleEventsReady) {
      getlegacyPeekavilleEvents();
    }

    return function cleanup() {
      mounted = false;
    };

    async function getlegacyPeekavilleEvents() {
      let results: ReportingMixpanelEvent[];
      if (
        state.schoolsListReady &&
        state.selectedSchoolYear &&
        state.selectedStartingDate &&
        state.selectedEndingDate
      ) {
        const selectedSchoolObject = state.schoolsList.list.find((s) => s.id === state.selectedSchool);

        if (!selectedSchoolObject)
          throw new Error(
            `Unexpected condition: could not find a matching school object for selected school in report`,
          );
        const resultsRaw = await queryMixpanelByGroup(
          getLegacySchoolPeekavilleScript(
            state.selectedStartingDate,
            state.selectedEndingDate,
            selectedSchoolObject.combineIds || selectedSchoolObject.id,
          ),
          'mypeekaville',
          tableOverrideSuffix,
        );

        results = resultsRaw;
      }

      if (mounted) {
        setState((prevState) => ({
          ...prevState,
          legacyPeekavilleEvents: results,
          legacyPeekavilleEventsReady: true,
        }));
      }
    }
  }, [state.selectedSchool, state.selectedSchoolYear, state.selectedStartingDate, state.selectedEndingDate]);

  //get new mixpanel DB analytics data

  useEffect(() => {
    let mounted = true;
    run();

    return function cleanup() {
      mounted = false;
    };

    async function run() {
      const selectedStartingDate = state.selectedStartingDate;
      const selectedEndingDate = state.selectedEndingDate;

      const storageKey = `${state.selectedSchool}-newMixpanelEvents-from-${selectedStartingDate}-to-${selectedEndingDate}`;

      if (state.selectedSchool && state.schoolsListReady) {
        const data = await getLocalForage(storageKey);
        if (!data) {
          doQuery();
        } else {
          if (mounted) {
            setState((prevState) => ({
              ...prevState,
              newMixpanelEvents: data,
              newMixpanelEventsReady: true,
            }));
          }
        }
      }

      async function doQuery() {
        const selectedSchoolObject = state.schoolsList.list.find((s) => s.id === state.selectedSchool);

        if (!selectedSchoolObject)
          throw new Error(
            `Unexpected condition: could not find a matching school object for selected school in report`,
          );

        try {
          const result = await queryMixpanelByGroupV2(
            getUpdatedSchoolUnifiedMoodScript(
              selectedStartingDate,
              selectedEndingDate,
              selectedSchoolObject.combineIds || selectedSchoolObject.id,
            ),
            'unified',
            tableOverrideSuffix,
          );

          await setLocalForage(storageKey, result, 60);

          if (mounted) {
            setState((prevState) => ({
              ...prevState,
              newMixpanelEvents: result,
              newMixpanelEventsReady: true,
            }));
          }
        } catch (error) {
          if (mounted) {
            setState((prevState) => ({
              ...prevState,
              errorMessage: (error as Error).message,
            }));
          }
        }
      }
    }
  }, [state.selectedSchool, state.selectedSchoolYear, state.selectedStartingDate, state.selectedEndingDate]);

  //
  // get students' journal entries
  //
  useEffect(() => {
    let mounted = true;

    const selectedStartingDate = state.selectedStartingDate;
    const selectedEndingDate = state.selectedEndingDate;

    if (
      state.userListReady &&
      state.selectedSchool &&
      state.legacyPeekavilleEventsReady &&
      state.newMixpanelEventsReady &&
      !state.journalsMapReady
    ) {
      getJournals();
    }

    return function cleanup() {
      mounted = false;
    };

    async function getJournals() {
      const storageKey = `${state.selectedSchool}-journals-from-${selectedStartingDate}-to-${selectedEndingDate}`;
      const map = await getLocalForage(storageKey);

      if (map && mounted) {
        return setState((prevState) => ({
          ...prevState,
          journalsMap: map,
          journalsMapReady: true,
        }));
      }

      const DBtoQuery = getMixpanelDatabaseByDate(dateRange.from as Date, dateRange.to as Date);
      if (DBtoQuery === 'newDB') {
        await setLocalForage(storageKey, state.newMixpanelEvents, 1440);
        if (mounted) {
          setState((prevState) => ({
            ...prevState,
            journalsMap: state.newMixpanelEvents as [],
            journalsMapReady: true,
          }));
        }
      } else if (DBtoQuery === 'legacyDB') {
        // filter out students who are not active, since querying
        // dynamoDB is expensive
        const studentIdToLoginsMap = getStudentsToLoginsMap([
          ...state.legacyPeekavilleEvents,
          ...state.legacyPlatformEvents,
        ]);
        // query the database for each student
        const students = state.userList.list.filter(
          (u) => u.type === 'Student' && studentIdToLoginsMap[u.userId as keyof typeof studentIdToLoginsMap],
        );
        const journalsArray: PeekavilleJournalEntry[] = [];
        let i = 0;
        while (i < students.length) {
          const journals = await getJournalsFromDb(students[i].userId, tableOverrideSuffix);

          journalsArray.push(...Object.values(journals));
          if (mounted) {
            setState((prevState) => ({
              ...prevState,
              journalsMapLoadProgress: `${i + 1} of ${students.length}`,
              numOfStudentJournalData: students.length,
              numOfJournalDataRetrived: i + 1,
            }));
          }

          i += 1;
        }

        await setLocalForage(storageKey, journalsArray, 1440);

        if (mounted) {
          setState((prevState) => ({
            ...prevState,
            journalsMap: journalsArray as [],
            journalsMapReady: true,
          }));
        }
      } else if (DBtoQuery === 'mixed') {
        // filter out students who are not active, since querying
        // dynamoDB is expensive
        const studentIdToLoginsMap = getStudentsToLoginsMap([
          ...state.legacyPeekavilleEvents,
          ...state.legacyPlatformEvents,
        ]);
        // query the database for each student
        const students = state.userList.list.filter(
          (u) => u.type === 'Student' && studentIdToLoginsMap[u.userId as keyof typeof studentIdToLoginsMap],
        );
        let i = 0;
        const journalsArray: PeekavilleJournalEntry[] = [];

        while (i < students.length) {
          const journals = await getJournalsFromDb(students[i].userId, tableOverrideSuffix);
          journalsArray.push(...Object.values(journals));
          if (mounted) {
            setState((prevState) => ({
              ...prevState,
              journalsMapLoadProgress: `${i + 1} of ${students.length}`,
              numOfStudentJournalData: students.length,
              numOfJournalDataRetrived: i + 1,
            }));
          }

          i += 1;
        }
        const combinedJournalsMap = [...journalsArray, ...state.newMixpanelEvents];
        await setLocalForage(storageKey, combinedJournalsMap, 1440);

        if (mounted) {
          setState((prevState) => ({
            ...prevState,
            journalsMap: combinedJournalsMap as [],
            journalsMapReady: true,
          }));
        }
      }
    }
  }, [
    state.legacyPeekavilleEvents,
    state.legacyPeekavilleEventsReady,
    state.newMixpanelEvents,
    state.newMixpanelEventsReady,
    state.userList.schoolId,
    state.selectedSchool,
    state.journalsMapReady,
    state.userListReady,
    state.userList.list,
    state.selectedSchoolYear,
    state.selectedStartingDate,
    state.selectedEndingDate,
  ]);

  //
  // disable drop downs when data is being retrieved
  //
  useEffect(() => {
    if (
      (!state.districtsListReady ||
        !state.schoolsListReady ||
        !state.userListReady ||
        !state.legacyPlatformEventsReady ||
        !state.legacyPeekavilleEventsReady ||
        !state.newMixpanelEventsReady ||
        !state.journalsMapReady) &&
      !state.errorMessage
    ) {
      return setState((prevState) => ({
        ...prevState,
        disableChangeLocation: true,
      }));
    }

    return setState((prevState) => ({
      ...prevState,
      disableChangeLocation: false,
    }));
  }, [
    state.districtsListReady,
    state.schoolsListReady,
    state.userListReady,
    state.legacyPlatformEventsReady,
    state.legacyPeekavilleEventsReady,
    state.newMixpanelEventsReady,
    state.journalsMapReady,
    state.errorMessage,
  ]);

  //
  // set error conditions
  //
  useEffect(() => {
    if (userProfile && !userProfile.district && !isUserHasKey('KEY_PEEKAPAK_MANAGEMENT')) {
      setState((prevState) => ({
        ...prevState,
        disableChangeLocation: false,
        errorMessage: 'No data available',
      }));
    }

    if (userProfile?.district && !isUserHasKey('KEY_ADMINISTRATOR_REPORTS_SELECT_SCHOOL') && !userProfile.schoolId) {
      setState((prevState) => ({
        ...prevState,
        disableChangeLocation: false,
        errorMessage: 'No data available',
      }));
    }

    if (state.schoolsListReady && state.schoolsList.list.length === 0) {
      setState((prevState) => ({
        ...prevState,
        disableChangeLocation: false,
        errorMessage: 'No school data applicable',
      }));
    }
  }, [userProfile, state.schoolsListReady, state.schoolsList]);

  const filterDataByDate = useCallback(async () => {
    if (state.journalsMapReady) {
      const journals = state.journalsMap.filter(
        (e) => new Date(e.createdAt) >= dateRange.from && new Date(e.createdAt) <= dateRange.to,
      );
      const dateRangePrevious = getPreviousDateRange(dateRange);
      const journalsLastPeriodToCompare = state.journalsMap.filter(
        (e) => new Date(e.createdAt) >= dateRangePrevious.from && new Date(e.createdAt) <= dateRangePrevious.to,
      );
      setDateFilteredJournals(journals);
      setDateFilteredJournalsPrevious(journalsLastPeriodToCompare);
    }
  }, [dateRange, selectedPeriodType, state.journalsMap, state.journalsMapReady]);

  useEffect(() => {
    filterDataByDate();
  }, [dateRange, selectedPeriodType, state.journalsMap, state.journalsMapReady]);

  useEffect(() => {
    setState((prevState) => ({
      ...prevState,
      ...getCommonInitialState(),
      ...getDistrictInitialState(),
      ...getSchoolInitialState(),
    }));
  }, [state.selectedSchoolYear, state.selectedStartingDate, state.selectedEndingDate]);

  async function onSelectDistrict(eventKey: string | null) {
    if (state.districtsListReady && state.districtsList.length > 0) {
      const selectedDistrict = eventKey || '';
      setState((prevState) => ({
        ...prevState,
        ...getCommonInitialState(),
        ...getSchoolInitialState(),
        selectedDistrict,
      }));
      const storageKey = 'selectedDistrict';
      await setLocalForage(storageKey, selectedDistrict);
    } else {
      console.warn(`%cDistrict List is Missing`, 'background: black; color:yellow');
    }
  }

  async function onSelectSchool(eventKey: string) {
    if (state.schoolsListReady && state.schoolsList.list.length > 0) {
      const district = state.selectedDistrict || userProfile?.district;
      const selectedSchool = eventKey;

      setState((prevState) => ({
        ...prevState,
        ...getCommonInitialState(),
        ...getSchoolInitialState(),
        selectedSchool,
      }));
      const storageKey = `${district}-selectedSchool`;
      await setLocalForage(storageKey, selectedSchool);
    } else {
      console.warn(`%cSchool List is Missing`, 'background: black; color:yellow');
    }
  }

  async function onSelectSchoolYear(schoolYear: string) {
    const { beginningTimeOfSchoolYear, endingTimeOfSchoolYear } = getBeginningAndEndingOfSchoolYear(schoolYear);
    setState((prevState) => ({
      ...prevState,
      selectedSchoolYear: schoolYear,
      selectedStartingDate: beginningTimeOfSchoolYear.toISOString().split('T')[0],
      selectedEndingDate: endingTimeOfSchoolYear.toISOString().split('T')[0],
      ...getCommonInitialState(),
    }));
    setDateRange({
      from: beginningTimeOfSchoolYear,
      to: endingTimeOfSchoolYear,
    });
    setSelectedPeriodType('yearly');
  }

  async function onReload() {
    await flushLocalForage();
    setState((prevState) => ({
      ...prevState,
      ...getCommonInitialState(),
      ...getTimePeriodInitialState(),
      ...getDistrictInitialState(),
    }));
  }

  if (!userProfile || !state.schoolsListReady) {
    return <SpinnerOverlay isShow />;
  }

  return (
    <div className={styles.container}>
      <div className={styles.centeringContainer}>
        <div className={styles.districtAndSchoolSelectors}>
          <div className={styles.titleAndReloadButton}>
            <h1>School Wide Mood Check-in</h1>
            <Icon className={styles.reloadIcon} icon='zondicons:reload' onClick={onReload} />
          </div>
          <DistrictSelector
            isPeekapakAdmin={!!isUserHasKey('KEY_PEEKAPAK_MANAGEMENT')}
            districtsList={state.districtsList}
            onReload={onReload}
            onSelectDistrict={onSelectDistrict}
            initialValue={state.selectedDistrict}
            currentlySelectedDistrict={state.selectedDistrict}
            disableChangeLocation={state.disableChangeLocation || false}
          />
          <SchoolSelector
            isDistrictAdmin={!!isUserHasKey('KEY_ADMINISTRATOR_REPORTS_SELECT_SCHOOL')}
            schoolsList={state.schoolsList}
            onSelectSchool={onSelectSchool}
            initialValue={state.selectedSchool}
            currentlySelectedSchool={state.selectedSchool}
            disableChangeLocation={state.disableChangeLocation || false}
          />
          <SchoolYearSelector
            selectedSchoolYear={state.selectedSchoolYear}
            onSelectSchoolYear={onSelectSchoolYear}
            disabled={state.disableChangeLocation || false}
          />
        </div>
        <PeriodSelector
          selectedPeriod={selectedPeriodType}
          handleClickPeriod={(period: string) => handleChangeTypeOfPeriod(period)}
          disableChangeTypeOfPeriod={state.disableChangeLocation || false}
        />
        {!state.legacyPeekavilleEventsReady ||
        !state.legacyPlatformEventsReady ||
        !state.newMixpanelEventsReady ||
        !dateFilteredJournals ||
        (!state.journalsMapReady && state.journalsMapLoadProgress === '') ? (
          <div className={styles.loadingContainer}>
            <Spinner />
          </div>
        ) : !state.journalsMapReady && state.journalsMapLoadProgress !== '' ? (
          <div className={styles.loadingContainer}>
            <LoadingBar
              title={'Loading...'}
              width={500}
              totalNum={state.numOfStudentJournalData || 0}
              numComplete={state.numOfJournalDataRetrived || 0}
              barColor='#012a73'
            />
          </div>
        ) : (
          <div className={styles.chartsContainer}>
            <ChartSegmentElementary
              isLoading={
                !state.journalsMapReady ||
                !state.legacyPeekavilleEventsReady ||
                !state.legacyPlatformEventsReady ||
                !state.newMixpanelEventsReady ||
                !dateFilteredJournals
              }
              data={dateFilteredJournals}
              previousPeriodData={dateFilteredJournalsPrevious}
              dateRange={dateRange}
              selectedPeriod={selectedPeriodType}
              shiftPeriod={(direction: string) => getShiftedDateRange(direction)}
              shouldShowNavArrowLeft={shouldShowNavArrowLeft}
              shouldShowNavArrowRight={shouldShowNavArrowRight}
            />
            <ChartSegmentSecondary
              isLoading={
                !state.journalsMapReady ||
                !state.legacyPeekavilleEventsReady ||
                !state.legacyPlatformEventsReady ||
                !state.newMixpanelEventsReady ||
                !dateFilteredJournals
              }
              data={dateFilteredJournals}
              previousPeriodData={dateFilteredJournalsPrevious}
              dateRange={dateRange}
              selectedPeriod={selectedPeriodType}
              shiftPeriod={(direction: string) => getShiftedDateRange(direction)}
              shouldShowNavArrowLeft={shouldShowNavArrowLeft}
              shouldShowNavArrowRight={shouldShowNavArrowRight}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default MoodboardSchoolAdmin;
